import React from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { getUserInfo } from '../../redux/selectors'
import { fetchContact, updateContact, saveContact, saveSubscription, saveEmail, saveDeactivate } from '../../redux/actions'
import ProfileInfoComponent from './ProfileInfoComponent'
import { isEmpty, cloneDeep } from 'lodash'
import { requesterEmailAddress } from '../../libs/utils'

class ProfileInfoContainer extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      editing: false,
      profileInfoChanged: false,
      contact: this.props.contact,
      originalContact: null,
      originalSubscription: null,
      updates: {
        emailAddress: requesterEmailAddress(this.props),
        products: []
      } // products example: [{name: 'Biologics', regions: [{name: 'US', operationType: 'Add'}]}]
    }

    this.toggleEditMode = () => this.setState({editing: !this.state.editing})
    this.saveContact = () => this.props.saveContact(this.state.contact)

    this.requestEmailEdit = (oldEmail, newEmail) => {
      const emailAddress = requesterEmailAddress(this.props)
      this.props.saveEmail({emailAddress, newEmailAddress: newEmail})
    }

    this.setSubscriptionToDefault = () => {
      this.props.updateContact({
        ...this.state.contact,
        subscription: cloneDeep(this.state.originalSubscription)
      })
      return this.setState({updates: {emailAddress: requesterEmailAddress(this.props), products: []}})
    }

    this.updateContact = event => {
      const field = event.target && event.target.id
      const value = event.target && event.target.value
      if (!field) return
      this.props.updateContact({
        ...this.state.contact,
        [field]: value
      })
      this.setState(prevState => ({profileInfoChanged: prevState.profileInfoChanged || prevState.originalContact[field] !== value}))
    }

    this.setContactToDefault = () => {
      this.props.updateContact(cloneDeep(this.state.originalContact))
      this.setState(prevState => ({profileInfoChanged: false}))
    }

    this.toggleContactStatus = () => this.props.updateContact({
      ...this.state.contact,
      subscription: {
        ...this.state.contact.subscription,
        contactStatus: this.state.contact.subscription.contactStatus.toLowerCase() === 'active'
          ? 'Pending Removal'
          : 'Active'
      }
    })

    this.toggleProductStatus = (productGroupName, productName) => event => {
      const newSubscription = cloneDeep(this.state.contact.subscription)
      const productGroupIndex = newSubscription.productGroups.findIndex(x => x.name === productGroupName)
      const productIndex = newSubscription.productGroups[productGroupIndex].products.findIndex(x => x.name === productName)
      const regions = newSubscription.productGroups[productGroupIndex].products[productIndex].regions
      const newUpdates = cloneDeep(this.state.updates)
      const productUpdate = {
        productGroupName,
        name: productName,
        regions: []
      }

      for (let i = 0; i < regions.length; i++) {
        if (!regions[i].purchased) continue

        const originalRegionStatus = this.state.originalSubscription.productGroups[productGroupIndex].products[productIndex].regions[i].accessStatus

        if (event.target.checked) {
          if (originalRegionStatus === 'no access') productUpdate.regions.push({name: regions[i].name, operationType: 'Add'})
          regions[i].accessStatus = originalRegionStatus === 'access granted' ? 'access granted' : 'access pending'
          regions[i].operationType = 'Add'
        } else {
          if (originalRegionStatus !== 'no access') productUpdate.regions.push({name: regions[i].name, operationType: 'Remove'})
          regions[i].accessStatus = originalRegionStatus === 'no access' ? 'no access' : 'access pending'
          regions[i].operationType = 'Remove'
        }
      }
      const updateIndex = newUpdates.products.findIndex(x => x.name === productName)
      if (updateIndex >= 0) {
        if (isEmpty(productUpdate.regions)) newUpdates.products.splice(updateIndex, 1)
        else newUpdates.products[updateIndex] = productUpdate
      } else {
        if (!isEmpty(productUpdate.regions)) newUpdates.products.push(productUpdate)
      }

      this.setState({updates: newUpdates})

      return this.props.updateContact({...this.state.contact, subscription: newSubscription})
    }

    this.toggleRegionStatus = (productGroupName, productName, regionName) => event => {
      const newSubscription = cloneDeep(this.state.contact.subscription)
      const productGroupIndex = newSubscription.productGroups.findIndex(x => x.name === productGroupName)
      const productIndex = newSubscription.productGroups[productGroupIndex].products.findIndex(x => x.name === productName)
      const regions = newSubscription.productGroups[productGroupIndex].products[productIndex].regions
      const regionIndex = regions.findIndex(x => x.name === regionName)
      const originalRegionStatus = this.state.originalSubscription.productGroups[productGroupIndex].products[productIndex].regions[regionIndex].accessStatus
      const newUpdates = cloneDeep(this.state.updates)
      const regionUpdate = {
        name: regionName,
        operationType: ''
      }

      if (event.target.checked) {
        if (originalRegionStatus === 'no access') regionUpdate.operationType = 'Add'
        regions[regionIndex].accessStatus = originalRegionStatus === 'access granted' ? 'access granted' : 'access pending'
        regions[regionIndex].operationType = 'Add'
      } else {
        if (originalRegionStatus !== 'no access') regionUpdate.operationType = 'Remove'
        regions[regionIndex].accessStatus = originalRegionStatus === 'no access' ? 'no access' : 'access pending'
        regions[regionIndex].operationType = 'Remove'
      }

      const updateProductIndex = newUpdates.products.findIndex(x => x.name === productName)
      const updateRegionIndex = updateProductIndex >= 0 ? newUpdates.products[updateProductIndex].regions.findIndex(x => x.name === regionName) : -1
      if (updateProductIndex >= 0) {
        if (updateRegionIndex >= 0) {
          if (!regionUpdate.operationType) newUpdates.products[updateProductIndex].regions.splice(updateRegionIndex, 1)
          else newUpdates.products[updateProductIndex].regions[updateRegionIndex] = regionUpdate
          if (isEmpty(newUpdates.products[updateProductIndex].regions)) newUpdates.products.splice(updateProductIndex, 1)
        } else {
          if (regionUpdate.operationType) newUpdates.products[updateProductIndex].regions.push(regionUpdate)
        }
      } else {
        if (regionUpdate.operationType) newUpdates.products.push({productGroupName, name: productName, regions: [regionUpdate]})
      }

      this.setState({updates: newUpdates})

      return this.props.updateContact({...this.state.contact, subscription: newSubscription})
    }

    this.saveSubscription = () => this.props.saveSubscription(this.state.updates)

    this.saveDeactivate = (comments, removalExplanation, removalReason) => {
      const emailAddress = requesterEmailAddress(this.props)

      const deactivateRequest = {
        emailAddress,
        reason: comments,
        contacts: [{
          emailAddress,
          reason: removalExplanation || removalReason
        }]
      }
      this.props.saveDeactivate(deactivateRequest)
      return this.toggleContactStatus()
    }
  }

  componentDidMount () {
    document.title = 'User Profile'
    const emailAddress = requesterEmailAddress(this.props)
    this.props.fetchContact(emailAddress)
  }

  static getDerivedStateFromProps (nextProps, prevState) {
    if (!isEmpty(nextProps.contact) && isEmpty(prevState.originalSubscription) && isEmpty(prevState.originalContact)) {
      return {
        contact: nextProps.contact,
        originalSubscription: cloneDeep(nextProps.contact.subscription),
        originalContact: cloneDeep(nextProps.contact)
      }
    }
    return {
      contact: nextProps.contact,
      originalSubscription: prevState.originalSubscription,
      originalContact: prevState.originalContact
    }
  }

  render () {
    const { editing, contact, updates, originalSubscription, profileInfoChanged } = this.state
    return (
      <ProfileInfoComponent
        contact={contact}
        subscription={isEmpty(contact) || isEmpty(contact.subscription) ? null : contact.subscription}
        originalSubscription={originalSubscription}
        editing={editing}
        toggleEditMode={this.toggleEditMode}
        saveContact={this.saveContact}
        updateContact={this.updateContact}
        resetContact={this.setContactToDefault}
        requestEmailEdit={this.requestEmailEdit}
        profileInfoChanged={profileInfoChanged}
        updates={updates}
        setSubscriptionToDefault={this.setSubscriptionToDefault}
        toggleContactStatus={this.toggleContactStatus}
        toggleProductStatus={this.toggleProductStatus}
        toggleRegionStatus={this.toggleRegionStatus}
        saveSubscription={this.saveSubscription}
        saveDeactivate={this.saveDeactivate}
        manageEmailsView={this.props.manageEmailsView}
      />
    )
  }
}

ProfileInfoContainer.propTypes = {
  emailAddress: PropTypes.string.isRequired,
  contact: PropTypes.object,
  manageEmailsView: PropTypes.bool
}

const mapStateToProps = state => ({
  emailAddress: getUserInfo(state).emailAddress,
  contact: getUserInfo(state).contact
})

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    fetchContact,
    updateContact,
    saveContact,
    saveSubscription,
    saveDeactivate,
    saveEmail
  }, dispatch)
)

const ProfileInfoConnect = connect(mapStateToProps, mapDispatchToProps)(ProfileInfoContainer)

export default ProfileInfoConnect
